import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import styled from "styled-components";
import TableActionButton from "../../../../@ui/TableActionButton";
import { PublicTransportImportType } from "../../../../constant/PublicTransportImportType";
import PublicTransportService from "../../../../services/PublicTransportService";
import TriggerImportModal from "../../../modal/LayerAdmin/PublicTransport/TriggerImportModal";

const StyledIcon = styled(FontAwesomeIcon)`
  margin-right: 0.5rem;
`;

interface TriggerImportProps {
  service: PublicTransportService;
  loadingDownloads: boolean;
  hasDownloadsOutstanding: boolean;
  onSubmitted: () => void;
}

const TriggerImport = ({
  service,
  loadingDownloads,
  hasDownloadsOutstanding,
  onSubmitted,
}: TriggerImportProps) => {
  const [show, setShow] = useState(false);
  const [submitting, setIsSubmitting] = useState(false);
  const [errored, setErrored] = useState(false);

  const handleSubmit = (
    type: PublicTransportImportType,
    file?: File | null,
    quarter?: string,
    description?: string,
    additionalFiles?: {
      busArchive?: File | null;
      railArchive?: File | null;
      metroArchive?: File | null;
    },
    usTransitImportId?: string,
    transitLandImportId?: string
  ) => {
    setIsSubmitting(true);
    const t = Number(type) as PublicTransportImportType;
    switch (t) {
      case PublicTransportImportType.AtocCif: {
        service
          .StartAtocImport(file!)
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
      case PublicTransportImportType.Traveline: {
        service
          .StartTravelineImport()
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
      case PublicTransportImportType.TransitLand: {
        service
          .StartTransitLandImport()
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
      case PublicTransportImportType.Gtfs: {
        if (quarter === undefined || description === undefined) {
          return;
        }
        service
          .StartGtfsImport(file!, quarter, description)
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
      case PublicTransportImportType.NCSD: {
        service
          .StartNcsdImport(file!)
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
      case PublicTransportImportType.Translink: {
        if (
          !additionalFiles?.busArchive ||
          !additionalFiles?.railArchive ||
          !additionalFiles?.metroArchive
        ) {
          return;
        }
        service
          .StartTranslinkImport(
            additionalFiles.busArchive,
            additionalFiles.railArchive,
            additionalFiles.metroArchive
          )
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
      case PublicTransportImportType.BoundaryVectorTiles: {
        service
          .StartBoundaryVectorTileImport()
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
      case PublicTransportImportType.UsTransit: {
        service
          .StartUsTransitImport()
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
      case PublicTransportImportType.UsTransitMerge: {
        service
          .StartUsTransitMerger(
            parseInt(usTransitImportId!),
            parseInt(transitLandImportId!)
          )
          .then(() => {
            setShow(false);
            onSubmitted();
          })
          .catch(() => {
            setErrored(true);
          });
        break;
      }
    }
  };

  return (
    <div className="mr-1 w-100 text-end mb-1">
      <div
        title={
          loadingDownloads
            ? "Searching for in progress downloads..."
            : hasDownloadsOutstanding
            ? "There is a download for this quarter still running!"
            : ""
        }
      >
        <TableActionButton
          onClick={() => setShow(true)}
          disabled={hasDownloadsOutstanding || loadingDownloads}
        >
          <StyledIcon icon={faPlus} />
          Trigger Import
        </TableActionButton>
      </div>
      <TriggerImportModal
        onSubmit={handleSubmit}
        onClose={() => setShow(false)}
        show={show}
        isSubmitting={submitting}
        hasErrored={errored}
      />
    </div>
  );
};

export default TriggerImport;
